import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Button,
  Drawer,
  Group,
  NumberInput,
  Select,
  Stack,
  Switch,
  TextInput,
} from '@mantine/core';
import { DateTimePicker } from '@mantine/dates';
import { useForm } from '@mantine/form';
import { notifications } from '@mantine/notifications';
import { isAxiosError } from 'axios';
import { useMemo } from 'react';
import { PROMOTION_TYPE, type PromotionDto } from './types';
import { useCreatePromotion, useUpdatePromotion } from './promotionsApi';
import { useItems } from '../items/itemsApi';

interface Props {
  opened: boolean;
  onClose: () => void;
  promotion: PromotionDto | null;
}

interface FormValues {
  code: string;
  nameAr: string;
  nameEn: string;
  type: number;
  discountPercent: number;
  discountAmount: number;
  minOrderSubtotal: number;
  targetItemId: string | null;
  bogoBuyQty: number;
  bogoFreeQty: number;
  activeFrom: Date | null;
  activeTo: Date | null;
  isActive: boolean;
}

export function PromotionFormDrawer({ opened, onClose, promotion }: Props) {
  const { t, i18n } = useTranslation();
  const create = useCreatePromotion();
  const update = useUpdatePromotion();
  const submitting = create.isPending || update.isPending;
  const editing = promotion !== null;
  const drawerPosition = i18n.language === 'ar' ? 'left' : 'right';

  const form = useForm<FormValues>({
    initialValues: {
      code: '',
      nameAr: '',
      nameEn: '',
      type: PROMOTION_TYPE.PERCENTAGE_OFF_ORDER,
      discountPercent: 10,
      discountAmount: 10,
      minOrderSubtotal: 0,
      targetItemId: null,
      bogoBuyQty: 2,
      bogoFreeQty: 1,
      activeFrom: null,
      activeTo: null,
      isActive: true,
    },
    validate: {
      code: (v) => (v.trim() ? null : t('common.errors.required')),
      nameAr: (v) => (v.trim() ? null : t('common.errors.required')),
      discountPercent: (v, vals) =>
        vals.type !== PROMOTION_TYPE.PERCENTAGE_OFF_ORDER
          ? null
          : v > 0 && v <= 100
            ? null
            : t('promotions.form.percentRange'),
      discountAmount: (v, vals) =>
        vals.type === PROMOTION_TYPE.AMOUNT_OFF_ORDER && !(v > 0)
          ? t('promotions.form.amountPositive')
          : null,
      targetItemId: (v, vals) =>
        vals.type === PROMOTION_TYPE.BOGO_SAME_ITEM && !v
          ? t('common.errors.required')
          : null,
    },
  });

  const itemsQuery = useItems();
  const itemOptions = useMemo(
    () =>
      (itemsQuery.data ?? []).map((it) => ({
        value: it.itemId,
        label: `${it.sku} · ${it.nameAr}`,
      })),
    [itemsQuery.data],
  );

  useEffect(() => {
    if (!opened) return;
    form.setValues({
      code: promotion?.code ?? '',
      nameAr: promotion?.nameAr ?? '',
      nameEn: promotion?.nameEn ?? '',
      type: promotion?.type ?? PROMOTION_TYPE.PERCENTAGE_OFF_ORDER,
      discountPercent: promotion?.discountPercent ?? 10,
      discountAmount: promotion?.discountAmount ?? 10,
      minOrderSubtotal: promotion?.minOrderSubtotal ?? 0,
      targetItemId: promotion?.targetItemId ?? null,
      bogoBuyQty: promotion?.bogoBuyQty ?? 2,
      bogoFreeQty: promotion?.bogoFreeQty ?? 1,
      activeFrom: promotion?.activeFrom ? new Date(promotion.activeFrom) : null,
      activeTo: promotion?.activeTo ? new Date(promotion.activeTo) : null,
      isActive: promotion?.isActive ?? true,
    });
    form.resetDirty();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opened, promotion]);

  const submit = async (values: FormValues) => {
    const isBogo = values.type === PROMOTION_TYPE.BOGO_SAME_ITEM;
    const isAmount = values.type === PROMOTION_TYPE.AMOUNT_OFF_ORDER;
    const isPercent = values.type === PROMOTION_TYPE.PERCENTAGE_OFF_ORDER;
    const payload = {
      code: values.code.trim().toUpperCase(),
      nameAr: values.nameAr.trim(),
      nameEn: values.nameEn.trim() || null,
      type: values.type,
      discountPercent: isPercent ? values.discountPercent : 0,
      discountAmount: isAmount ? values.discountAmount : 0,
      minOrderSubtotal: values.minOrderSubtotal,
      targetItemId: isBogo ? values.targetItemId : null,
      bogoBuyQty: isBogo ? values.bogoBuyQty : 0,
      bogoFreeQty: isBogo ? values.bogoFreeQty : 0,
      activeFrom: values.activeFrom?.toISOString() ?? null,
      activeTo: values.activeTo?.toISOString() ?? null,
    };
    try {
      if (editing) {
        await update.mutateAsync({
          id: promotion.promotionId,
          request: { ...payload, isActive: values.isActive },
        });
      } else {
        await create.mutateAsync(payload);
      }
      notifications.show({
        color: 'green',
        title: editing ? t('common.toasts.updated') : t('common.toasts.created'),
        message: payload.code,
      });
      onClose();
    } catch (err) {
      notifications.show({
        color: 'red',
        title: t('common.toasts.errorTitle'),
        message: extractApiMessage(err) ?? t('common.toasts.errorBody'),
      });
    }
  };

  return (
    <Drawer
      opened={opened}
      onClose={onClose}
      title={editing ? t('promotions.form.editTitle') : t('promotions.form.createTitle')}
      position={drawerPosition}
      size="md"
      padding="lg"
    >
      <form onSubmit={form.onSubmit(submit)}>
        <Stack gap="md">
          <TextInput
            label={t('promotions.form.codeLabel')}
            description={t('promotions.form.codeHelp')}
            placeholder="HAPPY10"
            withAsterisk
            className="font-mono"
            {...form.getInputProps('code')}
          />
          <TextInput
            label={t('promotions.form.nameArLabel')}
            withAsterisk
            placeholder={t('promotions.form.nameArPlaceholder')}
            {...form.getInputProps('nameAr')}
          />
          <TextInput
            label={t('promotions.form.nameEnLabel')}
            placeholder={t('promotions.form.nameEnPlaceholder')}
            {...form.getInputProps('nameEn')}
          />
          <Select
            label={t('promotions.form.typeLabel')}
            data={[
              {
                value: String(PROMOTION_TYPE.PERCENTAGE_OFF_ORDER),
                label: t('promotions.form.typePercent'),
              },
              {
                value: String(PROMOTION_TYPE.BOGO_SAME_ITEM),
                label: t('promotions.form.typeBogo'),
              },
              {
                value: String(PROMOTION_TYPE.AMOUNT_OFF_ORDER),
                label: t('promotions.form.typeAmount'),
              },
            ]}
            value={String(form.values.type)}
            onChange={(v) => form.setFieldValue('type', Number(v ?? PROMOTION_TYPE.PERCENTAGE_OFF_ORDER))}
            allowDeselect={false}
            withAsterisk
          />

          {form.values.type === PROMOTION_TYPE.PERCENTAGE_OFF_ORDER && (
            <NumberInput
              label={t('promotions.form.percentLabel')}
              description={t('promotions.form.percentHelp')}
              min={1}
              max={100}
              step={1}
              withAsterisk
              suffix="%"
              {...form.getInputProps('discountPercent')}
            />
          )}

          {form.values.type === PROMOTION_TYPE.AMOUNT_OFF_ORDER && (
            <NumberInput
              label={t('promotions.form.amountLabel')}
              description={t('promotions.form.amountHelp')}
              min={0}
              step={5}
              decimalScale={2}
              withAsterisk
              prefix="₪ "
              {...form.getInputProps('discountAmount')}
            />
          )}

          {form.values.type === PROMOTION_TYPE.BOGO_SAME_ITEM && (
            <>
              <Select
                label={t('promotions.form.targetItemLabel')}
                description={t('promotions.form.targetItemHelp')}
                data={itemOptions}
                value={form.values.targetItemId}
                onChange={(v) => form.setFieldValue('targetItemId', v)}
                searchable
                withAsterisk
                disabled={itemsQuery.isLoading}
              />
              <Group grow>
                <NumberInput
                  label={t('promotions.form.bogoBuyLabel')}
                  description={t('promotions.form.bogoBuyHelp')}
                  min={1}
                  max={20}
                  step={1}
                  withAsterisk
                  {...form.getInputProps('bogoBuyQty')}
                />
                <NumberInput
                  label={t('promotions.form.bogoFreeLabel')}
                  description={t('promotions.form.bogoFreeHelp')}
                  min={1}
                  max={20}
                  step={1}
                  withAsterisk
                  {...form.getInputProps('bogoFreeQty')}
                />
              </Group>
            </>
          )}
          <NumberInput
            label={t('promotions.form.minOrderLabel')}
            description={t('promotions.form.minOrderHelp')}
            min={0}
            step={5}
            decimalScale={2}
            prefix="₪ "
            {...form.getInputProps('minOrderSubtotal')}
          />

          <Group grow>
            <DateTimePicker
              label={t('promotions.form.activeFromLabel')}
              placeholder={t('promotions.form.alwaysOpen')}
              value={form.values.activeFrom}
              onChange={(value) => form.setFieldValue('activeFrom', value)}
              clearable
            />
            <DateTimePicker
              label={t('promotions.form.activeToLabel')}
              placeholder={t('promotions.form.noExpiry')}
              value={form.values.activeTo}
              onChange={(value) => form.setFieldValue('activeTo', value)}
              clearable
            />
          </Group>
          {editing && (
            <Switch
              label={t('promotions.form.activeLabel')}
              description={t('promotions.form.activeHelp')}
              checked={form.values.isActive}
              onChange={(event) => form.setFieldValue('isActive', event.currentTarget.checked)}
            />
          )}

          <Group justify="flex-end" gap="sm" mt="sm">
            <Button variant="default" onClick={onClose} disabled={submitting}>
              {t('common.cancel')}
            </Button>
            <Button type="submit" loading={submitting}>
              {editing ? t('common.save') : t('common.create')}
            </Button>
          </Group>
        </Stack>
      </form>
    </Drawer>
  );
}

function extractApiMessage(err: unknown): string | null {
  if (isAxiosError(err)) {
    const data = err.response?.data as { detail?: string; title?: string } | undefined;
    return data?.detail ?? data?.title ?? err.message;
  }
  return null;
}
